<script setup lang="ts">
import {computed} from "vue";
import {BellRing} from "@lucide/vue";
import {AgendaRenewalReminder} from "@/core/interfaces/AgendaRenewalReminder";
import {parseAgendaDate} from "@/agenda/lib/agendaTime";

/**
 * An upcoming-renewal reminder as the agenda shows it: placed on the day the
 * patient is warned, stating the renewal date it announces.
 */
const props = defineProps<{
    reminder: AgendaRenewalReminder;
    /** Narrow columns: the wording drops to the strict minimum. */
    compact?: boolean;
}>();

const emits = defineEmits<{
    (e: 'click'): void;
}>();

/** The person concerned: the beneficiary when the plan targets one. */
const patientName = computed(() => {
    return props.reminder.beneficiary?.name ?? props.reminder.customer.name ?? 'Patient';
});

const channelLabel = computed(() => props.reminder.channel === 'push' ? 'Notif' : 'SMS');

const renewLabel = computed(() => parseAgendaDate(props.reminder.renew_at).format('D MMM'));

const title = computed(() => {
    const renewal = parseAgendaDate(props.reminder.renew_at).format('dddd D MMMM');

    return `${channelLabel.value} de rappel à ${patientName.value} — renouvellement le ${renewal}`;
});
</script>

<template>
  <button
    type="button"
    class="flex w-full items-center gap-1.5 rounded-md bg-orange-light/50 px-2 py-1 text-left text-xs transition-colors hover:bg-orange-light"
    :title="title"
    @click="emits('click')"
  >
    <BellRing class="size-3.5 shrink-0 text-gray-dark" />
    <span class="shrink-0 font-medium">{{ channelLabel }}</span>
    <span class="truncate">{{ patientName }}</span>
    <span class="ml-auto shrink-0 tabular-nums text-gray-600">
      {{ compact ? renewLabel : `renouvelle le ${renewLabel}` }}
    </span>
  </button>
</template>
